/** `ai.formation` — a marching grid that swarm members take slots in and peel
 * off from. Members (archetypes listed in `ranks`, each carrying `ai.diver`)
 * claim the first free slot of their rank's rows on arrival, columns filled
 * centre-out; the grid sways side to side. A swarm may OPEN with a lead
 * sortie: the first seated member carrying `preferTag` leaves as soon as it
 * is seated. Otherwise, once every member has reached its slot, one seated
 * member is released on a sortie every interval (a named scalar, so
 * difficulty curves tighten it), preferring the tagged kind by a chance;
 * while a `rush` scalar is set (a bonus wave) sorties come at the rush
 * cadence instead. Slot positions travel to the divers as ONE `formation`
 * signal per tick; a release is a `sortie` signal. */
import type { JsonObject } from '../../json.js';
import { bool, id, int, list, num, obj } from '../schemaBuilders.js';
import { defineModule, num as read, type ModuleCtx } from '../module.js';
import type { EntityV1 } from '../world.js';

export type FormationRank = { archetype: string; rows: number[] };
export type FormationConfig = {
  cols: number; colGap: number; rowGap: number;
  /** Bottom edge (feet) of row 0. */
  top: number;
  swayAmp: number; swayPeriodTicks: number;
  ranks: FormationRank[];
  sortieEveryTicks: number;
  /** Scalar overriding `sortieEveryTicks` when set (escalation curves). */
  sortieScalar: string;
  /** While this scalar is ≥ 1, sorties come every `rushEveryTicks`. */
  rushScalar: string;
  rushEveryTicks: number;
  /** Members away (diving) at once, at most. */
  maxAway: number;
  preferTag: string;
  preferChance: number;
  /** A new swarm opens with a `preferTag` member's sortie as soon as one is seated. */
  leadSortie: boolean;
};

/** Ticks a primed clock holds: the next sortie may leave at once. */
const PRIMED = 1_000_000;

/** Column visiting order from the centre outwards (ties break left). */
export function centreOut(cols: number): number[] {
  const centre = (cols - 1) / 2;
  return Array.from({ length: cols }, (_, c) => c).sort((a, b) => Math.abs(a - centre) - Math.abs(b - centre) || a - b);
}

export const formation = defineModule<FormationConfig>({
  id: 'ai.formation', version: 1, attach: 'global', role: 'behaviour',
  summary: 'Swaying grid formation: members claim rank slots centre-out on arrival; an optional lead sortie, then one member released per interval once formed.',
  config: obj({
    cols: int(1, 12), colGap: num(0.3, 8), rowGap: num(0.3, 8), top: num(0, 64), swayAmp: num(0, 8), swayPeriodTicks: int(30, 3600),
    ranks: list(obj({ archetype: id(), rows: list(int(0, 11), 12, 1) }), 8, 1),
    sortieEveryTicks: int(1, 1800), sortieScalar: id(), rushScalar: id(), rushEveryTicks: int(1, 600), maxAway: int(1, 32),
    preferTag: id(), preferChance: num(0, 1), leadSortie: bool(),
  }),
  requires: ['ai.diver'], events: [], presentation: {},
  proofs: ['centre-out-fill', 'rank-rows', 'sway', 'lead-sortie', 'waits-until-formed', 'sortie-cadence', 'scalar-cadence', 'rush-cadence', 'prefers-tag', 'max-away'],
  animStates: () => [],
  mapArchetypeRefs: (c, rename) => ({ ...c, ranks: c.ranks.map(r => ({ ...r, archetype: rename(r.archetype) })) }),
  check(c, context) {
    const errors: string[] = [];
    for (const r of c.ranks) if (!context.archetypes[r.archetype]?.modules.includes('ai.diver')) errors.push(`${r.archetype} must use ai.diver`);
    if ((c.cols - 1) * c.colGap + 2 * c.swayAmp > context.world.w) errors.push('the swaying grid is wider than the world');
    return errors;
  },
  initGlobal(ctx) { Object.assign(ctx.state, { slots: {}, timer: PRIMED, led: !ctx.config.leadSortie }); },
  systems: {
    think(ctx) {
      const c = ctx.config, s = ctx.state;
      const slots = s.slots as Record<string, JsonObject>;
      const members = ctx.entities({ status: 'any' }).filter(e => c.ranks.some(r => r.archetype === e.archetype));
      const alive = new Set(members.map(e => e.id));
      for (const id of Object.keys(slots)) if (!alive.has(id)) delete slots[id];
      const taken = new Set(Object.values(slots).map(v => `${read(v, 'col')},${read(v, 'row')}`));
      const order = centreOut(c.cols);
      for (const e of members) {
        if (slots[e.id]) continue;
        const rank = c.ranks.find(r => r.archetype === e.archetype)!;
        const free = rank.rows.flatMap(row => order.map(col => ({ col, row }))).find(p => !taken.has(`${p.col},${p.row}`));
        if (!free) continue;
        slots[e.id] = free; taken.add(`${free.col},${free.row}`);
      }
      const sway = c.swayAmp * Math.sin((2 * Math.PI * ctx.world.tick) / c.swayPeriodTicks);
      const left = (ctx.world.bounds.w - (c.cols - 1) * c.colGap) / 2;
      const positions: JsonObject = {};
      for (const [id, p] of Object.entries(slots)) positions[id] = [left + read(p, 'col') * c.colGap + sway, c.top + read(p, 'row') * c.rowGap];
      ctx.signal('formation', null, null, { slots: positions });
      release(ctx, members);
    },
  },
});

function release(ctx: ModuleCtx<FormationConfig>, members: EntityV1[]): void {
  const c = ctx.config, s = ctx.state;
  const mode = (e: EntityV1) => ctx.peer(e, 'ai.diver')?.mode;
  const seated = members.filter(e => e.status === 'active' && mode(e) === 'hold');
  const preferred = seated.filter(e => e.tags.includes(c.preferTag));
  // An empty field re-arms the lead sortie for the next swarm.
  if (members.length === 0) { s.led = !c.leadSortie; s.timer = PRIMED; return; }
  if (s.led !== true && preferred.length) {
    s.led = true;
    sortie(ctx, preferred[ctx.randomInt(preferred.length)]!);
    return;
  }
  // Until every member has reached its slot the clock stands primed, so the
  // first sortie of a freshly formed swarm leaves at once.
  if (members.some(e => ctx.peer(e, 'ai.diver')?.formed !== true)) { s.timer = PRIMED; return; }
  const rush = ctx.scalar(c.rushScalar, 0) >= 1;
  const every = rush ? c.rushEveryTicks : Math.max(1, Math.round(ctx.scalar(c.sortieScalar, c.sortieEveryTicks)));
  s.timer = Math.min(PRIMED, read(s, 'timer') + 1);
  if (read(s, 'timer') < every) return;
  if (seated.length === 0 || members.filter(e => mode(e) === 'dive').length >= c.maxAway) return;
  const pool = preferred.length && ctx.random() < c.preferChance ? preferred : seated;
  sortie(ctx, pool[ctx.randomInt(pool.length)]!);
}

function sortie(ctx: ModuleCtx<FormationConfig>, member: EntityV1): void {
  ctx.signal('sortie', member, null, { dir: ctx.random() < 0.5 ? -1 : 1 });
  ctx.state.timer = 0;
}
